import React, { useEffect, useMemo, useState } from "react";
import { Header, Footer, Page } from "@/components/Shell";
import { API } from "@/lib/brand";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

export default function Reading() {
  const { token } = useParams();
  const nav = useNavigate();
  const [questionnaire, setQuestionnaire] = useState(null);
  const [step, setStep] = useState(0);
  const [closed, setClosed] = useState({});
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await axios.get(`${API}/questionnaire`);
      setQuestionnaire(data);
      const cached = localStorage.getItem(`mb_answers_${token}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        setClosed(parsed.closed || {}); setOpen(parsed.open || {});
      }
    })();
  }, [token]);

  const items = useMemo(() => {
    if (!questionnaire) return [];
    return [
      ...questionnaire.closed.map((q) => ({ ...q, kind: "closed" })),
      ...questionnaire.open.map((q) => ({ ...q, kind: "open" })),
    ];
  }, [questionnaire]);

  useEffect(() => {
    if (items.length) localStorage.setItem(`mb_answers_${token}`, JSON.stringify({ closed, open }));
  }, [closed, open, items.length, token]);

  if (!questionnaire) return <Page tone="ivory"><Header variant="ivory" /><div className="max-w-3xl mx-auto px-6 py-24 text-text-2">Un instant…</div></Page>;

  const total = items.length;
  const current = items[step];
  const progress = (step / total) * 100;
  const dimNumber = current.kind === "closed" ? Math.min(6, Math.ceil(current.id / 3)) : 6;
  const currentAnswered = current.kind === "closed" ? !!closed[current.id] : (open[current.key] || "").trim().length >= 3;

  const next = async () => {
    if (step < total - 1) { setStep(step + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }
    else {
      setSaving(true);
      try {
        await axios.post(`${API}/clients/${token}/answers`, {
          answers_closed: Object.fromEntries(Object.entries(closed).map(([k, v]) => [String(k), v])),
          answers_open: open,
        });
        nav(`/paiement/${token}`);
      } catch { toast.error("Impossible d'enregistrer vos réponses."); setSaving(false); }
    }
  };
  const prev = () => step > 0 && setStep(step - 1);

  return (
    <Page tone="ivory">
      <Header variant="ivory" />
      <div className="relative">
        <div className="absolute inset-0 bg-halo pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-10 pb-20 relative" data-testid="reading-page">
          <div className="flex items-baseline justify-between text-[12px] tracking-[0.28em] uppercase text-bronze">
            <div>Lecture {String(dimNumber).padStart(2,"0")} / 06</div>
            <div>Progression stratégique — {Math.round(progress)} %</div>
          </div>
          <div className="progress-track mt-4"><div className="progress-fill" style={{ width: `${progress}%` }} /></div>

          <div key={current.id} className="mt-16 fade-up" data-testid={`question-${current.id}`}>
            {current.kind === "closed" ? (
              <>
                <div className="overline mb-6">Dimension {String(dimNumber).padStart(2,"0")} — Question {String(current.id).padStart(2, "0")}</div>
                <h2 className="font-display text-[26px] md:text-[30px] leading-[1.25] text-ink max-w-3xl">{current.text}</h2>
                <div className="mt-12 grid md:grid-cols-2 gap-4 max-w-4xl">
                  {current.options.map((opt) => {
                    const isSelected = closed[current.id] === opt.text;
                    return (
                      <button key={opt.value} type="button" onClick={() => setClosed({ ...closed, [current.id]: opt.text })}
                        className={`scale-option ${isSelected ? "selected" : ""}`} data-testid={`answer-${current.id}-${opt.value}`}>
                        {opt.text}
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <div className="overline mb-6">Question ouverte {String(current.id).padStart(2, "0")}</div>
                <h2 className="font-display text-[26px] md:text-[30px] leading-tight text-ink max-w-3xl">{current.text}</h2>
                {current.helper && <p className="mt-4 text-text-2 text-[14px] italic max-w-3xl" data-testid={`helper-${current.key}`}>{current.helper}</p>}
                <textarea rows={7} className="input-lux mt-8" placeholder="Prenez le temps de répondre en quelques mots ou phrases…"
                  value={open[current.key] || ""} onChange={(e) => setOpen({ ...open, [current.key]: e.target.value })}
                  data-testid={`answer-open-${current.key}`} />
              </>
            )}
          </div>

          <div className="mt-14 flex items-center justify-between">
            <button onClick={prev} disabled={step === 0} className="btn-ghost disabled:opacity-30" data-testid="btn-prev">← Précédent</button>
            <button onClick={next} disabled={!currentAnswered || saving} className="btn-primary disabled:opacity-40" data-testid="btn-next">
              {step === total - 1 ? (saving ? "Enregistrement…" : "Préparer ma lecture") : "Suivant"}
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </Page>
  );
}
